import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ListCreateButton from 'components/buttons/ListCreateButton';
import {
    ExpandMoreRounded as ExpandMoreRoundedIcon,
    SwapHorizOutlined as SwapHorizOutlinedIcon,
} from 'components/icons';
import type { DataTableColumnDef } from 'components/tables/data-table';
import uniqBy from 'lodash/uniqBy';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';

import StatusChip, { ChipType } from 'components/@extended/StatusChip';
import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton';
import Loader from 'components/Loader';
import { Checkbox } from 'components/ui/checkbox';
import { cn } from 'utils/cn';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap.tsx';
import ListClamp from 'components/misc/ListClamp.tsx';
import { getFilterOptionsFromClassifier, mapToFilterOptions } from 'components/tables/list-table/filterUtil.ts';
import {
    DropdownFilterForm,
    FilterConfig,
    QuickFilterCheckboxGroupForm,
    SelectOption,
} from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { getLinks } from 'pages/matters/util';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetCorrespondenceListWithFiltersQuery } from 'store/api/correspondence';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useStartCorrespondenceExportMutation } from 'store/api/exports';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { RelationshipTypeCode } from 'types/contacts';
import {
    CorrespondenceDirectionType,
    CorrespondenceListElementDto,
    CorrespondenceStatusType,
} from 'types/correspondence';
import { EXPORT_CONFIG } from 'types/exports';
import { FeatureFlag } from 'types/features';
import { errorAlert, warningAlert } from 'utils/alert';
import { downloadFile } from 'utils/downloadFile.ts';
import { getClassifierText, t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { useHasPermissions } from 'utils/permissionUtil';
import { useAsyncExport } from 'utils/useAsyncExport';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import {
    BulkSelectCorrespondencesButton,
    CompleteCorrespondenceButton,
    isEligibleForCompletion,
} from './CompleteCorrespondenceButton';
import CorrespondenceMatterItem from './CorrespondenceMatterItem';
import CorrespondenceTableItemExpanded from './CorrespondenceTableItemExpanded';

const USED_CLASSIFIERS = [
    'CORRESPONDENCE_TYPE',
    'CORRESPONDENCE_DIRECTION',
    'CORRESPONDENCE_STATUS_TYPE',
    'CORRESPONDENCE_CATEGORY',
    'COUNTRY',
];

// The 8px margin gives each checkbox the 32px box the table's rows and header are measured around.
const CHECKBOX_BOX = 'm-2';

const filterCorrespondenceDirectionTypes = [CorrespondenceDirectionType.INCOMING, CorrespondenceDirectionType.OUTGOING];

const getCorrespondenceHref = (correspondence: CorrespondenceListElementDto) => `/correspondence/${correspondence.id}`;

const CorrespondenceTable = (): JSX.Element => {
    const navigateWithReturn = useNavigateWithReturnState();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });
    const [fetchData, { data, isFetching }] = useLazyGetCorrespondenceListWithFiltersQuery();
    const isPostalListExcelEnabled = useIsFeatureEnabled(FeatureFlag.CORRESPONDENCE_POSTAL_LIST_EXCEL);
    const hasPermissionToExportCorrespondence = useHasPermissions(['EXPORT.CORRESPONDENCE']);

    // Async export hook for full list export
    const { isBusy: isAsyncExportFetching, triggerManualDownload: triggerAsyncExport } = useAsyncExport(
        useStartCorrespondenceExportMutation,
        {
            entityType: 'CORRESPONDENCE',
            entityName: t('correspondence.export.entity-name'),
            fileName: EXPORT_CONFIG.CORRESPONDENCE.fileName,
        }
    );

    // Row selection for export
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    const toggleSelection = useCallback((id: number) => {
        setSelectedIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        const selectableItems = isPostalListExcelEnabled
            ? data?.content || []
            : (data?.content || []).filter(isEligibleForCompletion);
        const selectableIds = selectableItems.map((item) => item.id);
        const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

        if (allSelected) {
            // Deselect all selectable items
            setSelectedIds((prev) => {
                const newSet = new Set(prev);
                selectableIds.forEach((id) => newSet.delete(id));
                return newSet;
            });
        } else {
            // Select all selectable items
            setSelectedIds((prev) => {
                const newSet = new Set(prev);
                selectableIds.forEach((id) => newSet.add(id));
                return newSet;
            });
        }
    }, [data?.content, selectedIds, isPostalListExcelEnabled]);

    const [isExporting, setIsExporting] = useState(false);

    const triggerExportDownload = useCallback(async () => {
        const selectedItems = data?.content?.filter((item) => selectedIds.has(item.id)) || [];

        if (selectedItems.length === 0) {
            warningAlert(t('correspondence.export-no-selection'));
            return;
        }

        setIsExporting(true);
        try {
            await downloadFile(
                `/v1/correspondence/generate/?ids=${selectedItems.map((selected) => selected.id).join(',')}`,
                'correspondence.pdf'
            );

            // Clear selection after successful export
            setSelectedIds(new Set());
        } catch (error) {
            errorAlert(t('export-error'));
            console.error('Correspondence export error:', error);
        } finally {
            setIsExporting(false);
        }
    }, [data?.content, selectedIds]);

    // Check if all visible items are selected (considering eligibility when feature is disabled)
    const selectableItems = isPostalListExcelEnabled
        ? data?.content || []
        : (data?.content || []).filter(isEligibleForCompletion);
    const selectableIds = selectableItems.map((item) => item.id);
    const allSelectableSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
    const someSelectableSelected = selectableIds.some((id) => selectedIds.has(id));

    const columns = useMemo<DataTableColumnDef<CorrespondenceListElementDto>[]>(
        () => [
            {
                id: 'select',
                enableSorting: false,
                header: () => (
                    <Checkbox
                        className={CHECKBOX_BOX}
                        aria-label={t('forms.select-all')}
                        checked={allSelectableSelected}
                        indeterminate={someSelectableSelected && !allSelectableSelected}
                        onCheckedChange={toggleSelectAll}
                        disabled={selectableIds.length === 0}
                    />
                ),
                meta: {
                    // Checkbox / expander columns: sized to their content rather than taking a share
                    // of the flex pool.
                    fixedPx: 64,
                },
                cell: ({ row }: { row: { original: CorrespondenceListElementDto } }) => {
                    const isSelectable = isPostalListExcelEnabled || isEligibleForCompletion(row.original);
                    if (!isSelectable) {
                        return null;
                    }
                    return (
                        <Checkbox
                            className={CHECKBOX_BOX}
                            aria-label={row.original.fullName}
                            checked={selectedIds.has(row.original.id)}
                            onCheckedChange={() => toggleSelection(row.original.id)}
                        />
                    );
                },
            } as DataTableColumnDef<CorrespondenceListElementDto>,
            {
                header: t('correspondence.number-name-type'),
                accessorKey: 'number',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ cell }) => {
                    const { id, number, name, typeCode } = cell.row.original;
                    return (
                        <div className="flex flex-col">
                            <RouterLink
                                className="entity-link text-fg hover:text-link font-semibold no-underline hover:underline"
                                to={`/correspondence/${id}`}
                            >
                                {number ?? '-'}
                            </RouterLink>
                            <p className="text-body1">{name}</p>
                            <p className="text-body1">
                                {getClassifierText(classifiers?.['CORRESPONDENCE_TYPE'], typeCode)}
                            </p>
                        </div>
                    );
                },
            },
            {
                header: t('correspondence.incoming-outgoing'),
                accessorKey: 'directionTypeCode',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ cell }) => {
                    const { directionTypeCode } = cell.row.original;
                    return (
                        <p className="text-body1">
                            {getClassifierText(classifiers?.['CORRESPONDENCE_DIRECTION'], directionTypeCode)}
                        </p>
                    );
                },
            },
            {
                header: t('correspondence.date'),
                accessorKey: 'date',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ getValue }) => <DateDefaultNowrap dateValue={getValue()} />,
            },
            {
                header: t('correspondence.official-ref-number'),
                accessorKey: 'officialReference',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ cell }) => {
                    const { isOfficial, officialReference } = cell.row.original;
                    return (
                        <div className="flex flex-col">
                            <p className="text-body1">
                                {isOfficial ? <FormattedMessage id="correspondence.official" /> : '-'}
                            </p>
                            <p className="text-body1">{officialReference}</p>
                        </div>
                    );
                },
            },
            {
                header: t('correspondence.employee'),
                accessorKey: 'employees',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ cell }) => {
                    const { employees } = cell.row.original;
                    const mappedEmployees = uniqBy(
                        (employees || []).map((item) => item.employee),
                        (employee) => employee.id
                    );
                    return (
                        <p className="text-body1">
                            {mappedEmployees?.length ? getLinks('employee', mappedEmployees) : '-'}
                        </p>
                    );
                },
            },
            {
                header: t('correspondence.handled-status'),
                accessorKey: 'statusTypeCode',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ cell }) => {
                    const { isHandled, statusTypeCode, id } = cell.row.original;
                    const types: Record<CorrespondenceStatusType, ChipType> = {
                        [CorrespondenceStatusType.DRAFT]: 'warning',
                        [CorrespondenceStatusType.IN_PROGRESS]: 'success',
                        [CorrespondenceStatusType.COMPLETED]: 'neutral',
                    };
                    return (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center">
                                {/* Read-only: drawn at full strength, checked in grey. */}
                                <Checkbox
                                    id={`isHandled-${id}`}
                                    checked={isHandled}
                                    disabled
                                    className={cn(
                                        CHECKBOX_BOX,
                                        'data-checked:border-grey-300 data-checked:bg-grey-300 data-disabled:opacity-100'
                                    )}
                                />
                                <label htmlFor={`isHandled-${id}`} className="text-body1 text-primary block truncate">
                                    <FormattedMessage id="correspondence.handled" />
                                </label>
                            </div>
                            <div className="flex flex-nowrap">
                                <StatusChip
                                    type={types[statusTypeCode as CorrespondenceStatusType]}
                                    label={getClassifierText(
                                        classifiers?.['CORRESPONDENCE_STATUS_TYPE'],
                                        statusTypeCode
                                    )}
                                />
                            </div>
                        </div>
                    );
                },
            },
            {
                header: t('correspondence.connected-cases'),
                accessorKey: 'matters',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ cell }) => {
                    const { matters } = cell.row.original;
                    return (
                        <ListClamp
                            items={matters}
                            maxVisibleItems={1}
                            getKey={({ id }) => id}
                            emptyContent="-"
                            renderItem={(matter) => <CorrespondenceMatterItem key={matter.id} matter={matter} />}
                            sort={(a, b) => (a.fullName.toLocaleUpperCase() < b.fullName.toLocaleUpperCase() ? -1 : 0)}
                        />
                    );
                },
            },
            {
                id: 'expander',
                enableSorting: false,
                meta: {
                    fixedPx: 64,
                },
                cell: ({ row }) => (
                    <TableRowCallForActionButton
                        onClick={row.getToggleExpandedHandler()}
                        style={{ cursor: 'pointer' }}
                        aria-label={t(row.getIsExpanded() ? 'collapse-row' : 'expand-row')}
                        shape="rounded"
                    >
                        <ExpandMoreRoundedIcon
                            className={cn(
                                'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                row.getIsExpanded() && '-scale-y-100'
                            )}
                        />
                    </TableRowCallForActionButton>
                ),
            },
        ],
        [
            classifiers,
            selectedIds,
            allSelectableSelected,
            someSelectableSelected,
            selectableIds,
            toggleSelectAll,
            toggleSelection,
            isPostalListExcelEnabled,
        ]
    );

    const correspondenceDirectionTypeOptions = useMemo(
        () => mapToFilterOptions(filterCorrespondenceDirectionTypes, classifiers['CORRESPONDENCE_DIRECTION']),
        [classifiers]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'isHandled',
                    filters: [
                        {
                            tooltip: 'correspondence.tooltips.unprocessed-correspondence',
                            label: t('correspondence.filter.unprocessed-correspondence'),
                            argument: 'false',
                        },
                    ],
                    onChange: ({ isSelected, mainFilterState, handleMainFilterStateChange }) => {
                        const newMainState = { ...mainFilterState };
                        const mainFilterEntry = Object.entries(newMainState).find(
                            ([, filter]) => filter.selector === 'isHandled' && filter.type === 'dropdown'
                        );
                        if (mainFilterEntry) {
                            const mainF = mainFilterEntry[1] as DropdownFilterForm;
                            if (isSelected) {
                                mainF.selected = { label: t('no'), value: 'false' };
                            } else {
                                mainF.selected = null;
                            }
                            handleMainFilterStateChange(newMainState);
                        }
                    },
                },
                {
                    selector: 'isOfficial',
                    filters: [
                        {
                            label: t('correspondence.filter.official'),
                            argument: 'true',
                            tooltip: 'correspondence.tooltips.shows-marked-as-official',
                        },
                    ],
                },
                {
                    label: 'correspondence.filter.direction',
                    selector: 'directionTypeCode',
                    filters: correspondenceDirectionTypeOptions,
                },
                {
                    selector: 'statusTypeCode',
                    comperator: '=notlk=',
                    filters: [
                        {
                            label: t('correspondence.filter.hide-completed'),
                            argument: CorrespondenceStatusType.COMPLETED,
                            tooltip: 'correspondence.tooltips.hide-completed',
                        },
                    ],
                },
            ],
            filters: [
                // -------- DETAILS --------
                {
                    group: 'correspondence.filter.details',
                    type: 'text',
                    selector: 'name',
                    label: t('correspondence.filter.correspondence-name'),
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'multiselect',
                    selector: 'typeCode',
                    label: t('correspondence.filter.correspondence-type'),
                    options: getFilterOptionsFromClassifier(classifiers?.['CORRESPONDENCE_TYPE']),
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'checkboxgroup',
                    selector: 'directionTypeCode',
                    label: t('correspondence.filter.direction'),
                    filters: correspondenceDirectionTypeOptions,
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'daterange',
                    selector: 'date',
                    label: t('date-from'),
                    labelTo: t('date-to'),
                    shouldDisplayQuickPick: false,
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'daterange',
                    selector: 'documentDate',
                    label: t('correspondence.filter.document-date-from'),
                    labelTo: t('correspondence.filter.document-date-to'),
                    shouldDisplayQuickPick: false,
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'checkboxgroup',
                    selector: 'isOfficial',
                    filters: [
                        {
                            label: t('correspondence.filter.official'),
                            argument: 'true',
                            tooltip: 'correspondence.tooltips.shows-marked-as-official',
                        },
                    ],
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'text',
                    selector: 'officialReference',
                    label: t('correspondence.filter.official-reference-number'),
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'dynamicmultiselect',
                    selector: 'employees.employee.id',
                    label: t('correspondence.filter.sender-recipient-employee'),
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'dynamicmultiselect',
                    subType: 'contact',
                    selector: 'parties',
                    comperator: '=iscorrespondencecontact=',
                    label: t('correspondence.filter.sender-recipient-contact'),
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'text',
                    selector: 'parties.correspondencePartyAddress.address1',
                    label: t('address'),
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'text',
                    selector: 'parties.correspondencePartyAddress.cityName',
                    label: t('address-form.city'),
                    multiple: true,
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'multiselect',
                    selector: 'parties.correspondencePartyAddress.countryCode',
                    label: t('address-form.country'),
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'text',
                    selector: 'note',
                    label: t('correspondence.filter.correspondence-notes'),
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'dropdown',
                    selector: 'isHandled',
                    label: t('correspondence.filter.handled'),
                    comperator: '==',
                    options: [
                        {
                            label: t('yes'),
                            value: 'true',
                        },
                        {
                            label: t('no'),
                            value: 'false',
                        },
                    ],
                    onChange: (val, _groupSelector, quickFilters, handleQuickFilterChange) => {
                        const value = (val as SelectOption | null)?.value;
                        // Find the quick filter by searching for selector 'isHandled'
                        const quickFilterEntry = Object.entries(quickFilters).find(
                            ([, qf]) => qf.selector === 'isHandled'
                        );
                        if (quickFilterEntry) {
                            const [qfGroupSelector, qfFilter] = quickFilterEntry;
                            const checkboxGroup = qfFilter as QuickFilterCheckboxGroupForm;
                            const qfCheckbox = checkboxGroup.filters?.find((item) =>
                                item.key.startsWith('qf-isHandled:=in=false')
                            );
                            if ((value === 'true' || !value) && qfCheckbox?.isSelected) {
                                handleQuickFilterChange(false, qfGroupSelector, qfCheckbox.key);
                            } else if (value === 'false' && qfCheckbox && !qfCheckbox.isSelected) {
                                handleQuickFilterChange(true, qfGroupSelector, qfCheckbox.key);
                            }
                        }
                    },
                },
                {
                    group: 'correspondence.filter.details',
                    type: 'multiselect',
                    selector: 'statusTypeCode',
                    label: t('correspondence.filter.status'),
                    options: getFilterOptionsFromClassifier(classifiers?.['CORRESPONDENCE_STATUS_TYPE']),
                },
                // -------- Client/Case connections --------
                {
                    group: 'correspondence.filter.client-case-connections',
                    type: 'dynamicmultiselect',
                    subType: 'contact',
                    selector: 'parties.mainParty',
                    comperator: '=iscorrespondenceclient=',
                    label: t('correspondence.filter.client-name'),
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                        filters: `relationshipTypeCode=in=(${RelationshipTypeCode.CLIENT},${RelationshipTypeCode.LEAD})`,
                    },
                },
                {
                    group: 'correspondence.filter.client-case-connections',
                    type: 'dynamicmultiselect',
                    selector: 'matters.matter.id',
                    label: t('correspondence.filter.case-number-name'),
                    getOptionStartAdornment: renderMatterIndicators,
                    paginated: {
                        fetch: useLazyGetMattersListQuery,
                        searchField: ['fullName', 'clientParty.name'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'fullName',
                        sort: ['clientParty.name,asc', 'name,asc'],
                        groupByField: 'clientParty.name',
                    },
                },
            ],
            searchBarSelectors: [
                { selector: 'number' },
                { selector: 'name' },
                { selector: 'officialReference' },
                { selector: 'employees.employee.fullName' },
                { selector: 'matters.matter.fullName' },
                { selector: 'matters.matter.clientParty.name' },
                { selector: 'parties.correspondencePartyAddress.address1' },
                { selector: 'parties.correspondencePartyAddress.cityName' },
            ],
        }),
        [classifiers, correspondenceDirectionTypeOptions]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<CorrespondenceListElementDto>
            getRowHref={getCorrespondenceHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="correspondence"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="correspondence.correspondence"
            singularTextId="correspondence.correspondence"
            noEntriesId="correspondence.no-correspondence-found"
            noEntriesBodyId="correspondence.empty.body"
            noEntriesIcon={SwapHorizOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.CORRESPONDENCE']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/correspondence/new')}
                    labelId="correspondence.add-correspondence"
                    newLabelId="new-entity.correspondence"
                />
            }
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
            disableUrlState={false}
            renderExpandedContent={(row) => <CorrespondenceTableItemExpanded {...row} />}
            exportHookResults={[
                ...(hasPermissionToExportCorrespondence
                    ? [
                          {
                              labelKey: 'export',
                              isBusy: isAsyncExportFetching,
                              triggerManualDownload: triggerAsyncExport,
                          },
                      ]
                    : []),
                ...(isPostalListExcelEnabled
                    ? [
                          {
                              labelKey: 'correspondence.export-postal-list',
                              isBusy: isExporting,
                              triggerManualDownload: triggerExportDownload,
                          },
                      ]
                    : []),
            ]}
            renderSearchDescriptionLevelComponent={() => {
                const selectedCorrespondences = (data?.content || []).filter((c) => selectedIds.has(c.id));
                return (
                    <div className="ml-4 flex gap-2">
                        <BulkSelectCorrespondencesButton
                            correspondences={data?.content || []}
                            setSelectedIds={setSelectedIds}
                        />
                        <CompleteCorrespondenceButton correspondences={selectedCorrespondences} />
                    </div>
                );
            }}
        />
    );
};

export default CorrespondenceTable;
